import produce, { Draft } from 'immer';

import {
    APPLY_SEARCH_VALUE_FOR_REGIONS_SEARCH_EDITOR,
    ApplySearchValueForRegionsSearchEditor,
    CHANGE_SEARCH_VALUE_FOR_REGIONS_SEARCH_EDITOR,
    CHANGE_SUGGEST_ITEMS_FOR_REGIONS_SEARCH_EDITOR,
    ChangeSearchValueForRegionsSearchEditor,
    ChangeSuggestItemsForRegionsSearchEditor,
    CLEAR_REGIONS_SEARCH_EDITOR,
    DESELECT_SUGGEST_ITEM_FOR_REGIONS_SEARCH_EDITOR,
    DeselectSuggestItemForRegionsSearchEditor,
    INIT_REGIONS_SEARCH_EDITOR,
    RegionsSearchEditorAction,
    SELECT_SUGGEST_ITEM_FOR_REGIONS_SEARCH_EDITOR,
    SelectSuggestItemForRegionsSearchEditor
} from '../actions/regionsSearchEditor';
import { getDefaultRegionsSearchEditorState } from '../service/RegionsHelpers/RegionsHelpers';
import { StringHelpers } from '../service/StringHelpers/StringHelpers';
import { TreeHelpers } from '../service/TreeHelpers/TreeHelpers';
import { Region } from '../typings/common';
import { RegionsSearchEditorsState } from '../typings/regionsSearchEditor';
import { State } from '../typings/state';

export const changeSearchValueForRegionsSearchEditor = (
    draftState: Draft<RegionsSearchEditorsState>,
    action: ChangeSearchValueForRegionsSearchEditor,
    state: RegionsSearchEditorsState
): void => {
    const { editorId } = action;
    const editor = state[editorId];

    draftState[editorId] = {
        ...editor,
        regionsSearchValue: action.searchValue
    };
};

export const changeSuggestItemsForRegionsSearchEditor = (
    draftState: Draft<RegionsSearchEditorsState>,
    action: ChangeSuggestItemsForRegionsSearchEditor,
    state: RegionsSearchEditorsState
): void => {
    const { editorId, searchSuggestItems } = action;
    const editor = state[editorId];

    draftState[editorId] = {
        ...editor,
        searchSuggestItems
    };
};

export const selectSuggestItemForRegionsSearchEditor = (
    draftState: Draft<RegionsSearchEditorsState>,
    action: SelectSuggestItemForRegionsSearchEditor,
    state: RegionsSearchEditorsState
): void => {
    const { editorId, nodeId, regionTree } = action;
    const editor = state[editorId];
    const { selectedRegions } = editor;

    if (!regionTree) {
        return;
    }

    const newSelectedItem = TreeHelpers.find<Region>(regionTree, (node: Region) => {
        return node.id === nodeId;
    });

    if (!newSelectedItem) {
        return;
    }

    const isAlreadyHasItem = selectedRegions.some(selectedRegion => selectedRegion.val === nodeId);
    const editorClearedParams = {
        searchSuggestItems: [],
        regionsSearchValue: ''
    };

    if (isAlreadyHasItem) {
        draftState[editorId] = {
            ...editor,
            ...editorClearedParams
        };
        return;
    }

    draftState[editorId] = {
        ...editor,
        selectedRegions: [
            ...selectedRegions,
            {
                val: nodeId,
                text: newSelectedItem.name
            }
        ],
        ...editorClearedParams
    };
};

export const deselectSuggestItemForRegionsSearchEditor = (
    draftState: Draft<RegionsSearchEditorsState>,
    action: DeselectSuggestItemForRegionsSearchEditor,
    state: RegionsSearchEditorsState
): void => {
    const { editorId, nodeId } = action;
    const editor = state[editorId];
    const { selectedRegions } = editor;

    draftState[editorId] = {
        ...editor,
        selectedRegions: selectedRegions.filter(selectedRegion => selectedRegion.val !== nodeId)
    };
};

export const applySearchValueForRegionsSearchEditor = (
    draftState: Draft<RegionsSearchEditorsState>,
    action: ApplySearchValueForRegionsSearchEditor,
    state: RegionsSearchEditorsState
): void => {
    const { editorId, searchValue, regionTree } = action;
    const editor = state[editorId];
    const { selectedRegions } = editor;
    const trimmedSearchValue = searchValue.trim();

    if (!regionTree || trimmedSearchValue === '') {
        return;
    }

    const foundedRegionItem = TreeHelpers.find<Region>(regionTree, (node: Region) => {
        return StringHelpers.equalSubstring(node.name, trimmedSearchValue);
    });

    if (!foundedRegionItem) {
        return;
    }

    const isAlreadyHasItem = selectedRegions.some(selectedRegion => selectedRegion.val === foundedRegionItem.id);
    const editorClearedParams = {
        searchSuggestItems: [],
        regionsSearchValue: ''
    };

    if (isAlreadyHasItem) {
        draftState[editorId] = {
            ...editor,
            ...editorClearedParams
        };
        return;
    }

    draftState[editorId] = {
        ...editor,
        selectedRegions: [
            ...selectedRegions,
            {
                val: foundedRegionItem.id,
                text: foundedRegionItem.name
            }
        ],
        ...editorClearedParams
    };
};

export function regionsSearchEditor(
    state: RegionsSearchEditorsState = {},
    action: RegionsSearchEditorAction,
    fullState: State
): RegionsSearchEditorsState {
    return produce(state, (draftState: Draft<RegionsSearchEditorsState>) => {
        switch (action.type) {
            case INIT_REGIONS_SEARCH_EDITOR: {
                draftState[action.editorId] = {
                    ...action.value
                };
                break;
            }
            case CHANGE_SEARCH_VALUE_FOR_REGIONS_SEARCH_EDITOR: {
                changeSearchValueForRegionsSearchEditor(draftState, action, state);
                break;
            }

            case CHANGE_SUGGEST_ITEMS_FOR_REGIONS_SEARCH_EDITOR: {
                changeSuggestItemsForRegionsSearchEditor(draftState, action, state);
                break;
            }

            case SELECT_SUGGEST_ITEM_FOR_REGIONS_SEARCH_EDITOR: {
                selectSuggestItemForRegionsSearchEditor(draftState, action, state);
                break;
            }

            case DESELECT_SUGGEST_ITEM_FOR_REGIONS_SEARCH_EDITOR: {
                deselectSuggestItemForRegionsSearchEditor(draftState, action, state);
                break;
            }

            case APPLY_SEARCH_VALUE_FOR_REGIONS_SEARCH_EDITOR: {
                applySearchValueForRegionsSearchEditor(draftState, action, state);
                break;
            }

            case CLEAR_REGIONS_SEARCH_EDITOR: {
                draftState[action.editorId] = {
                    ...getDefaultRegionsSearchEditorState()
                };
                break;
            }

            default:
                break;
        }

        return draftState;
    });
}
